import { isAxiosError } from 'axios'

import { TruckbookApiResponseError } from 'repositories/TruckbookApiResponseError'

// WHY: BE (TB-1882) は「権限 4 区分いずれも無し」「非所属」「テナント不存在」を区別させないため、
// この 3 ケースすべてを 404 + code "917" の完全に同一のレスポンスで返す（テナント ID の
// 列挙オラクルを作らないため 403 と 404 を分けない）。code "2000" は JWT 有効だが
// ユーザーが DB に不存在（削除済み等）。どちらも再ログインでしか回復しない。
// NOTE: 下記のキー名は FE 側の呼び名。BE の errors[].type は 'Not found' という表示文字列であり、
// 将来変わりうるため判定には使わない（code のみで判定する）。
const TENANT_SETTINGS_LOGOUT_ERROR_CODE = {
  ERROR_TENANT_ID_INVALID: '917',
  USER_NOT_FOUND: '2000',
} as const

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
} as const

const tenantSettingsLogoutErrorCodes: readonly string[] = Object.values(
  TENANT_SETTINGS_LOGOUT_ERROR_CODE
)

const authErrorStatuses: readonly number[] = [HTTP_STATUS.UNAUTHORIZED, HTTP_STATUS.FORBIDDEN]

const getHttpStatus = (err: unknown): number | undefined => {
  if (err instanceof TruckbookApiResponseError) return err.httpStatus
  if (isAxiosError(err)) return err.response?.status
  return undefined
}

// NOTE: 名前どおり errors[0] しか見ない。/berths/tenant の 404 では BE が errors を必ず 1 要素で返すため
// 十分だが、errors[1] 以降に "917"/"2000" が入るエンドポイントには使えない（そのときは全要素走査が必要）。
const getFirstApiErrorCode = (err: unknown): string | undefined => {
  if (err instanceof TruckbookApiResponseError) return err.firstCode
  // NOTE: berthApiClient は createTruckbookApiErrorInterceptor 未配線のため、
  // 実運用・MSW テストともにここは AxiosError 形で届く。envelope 型ガードを通してから
  // errors[0].code を取り出すことで、ルーティング由来の 404（envelope を持たない）を除外する。
  const body = isAxiosError(err) ? err.response?.data : undefined
  if (TruckbookApiResponseError.isApiErrorResponseBody(body)) return body.data.errors[0]?.code
  return undefined
}

export const isAuthError = (err: unknown): boolean => {
  const status = getHttpStatus(err)
  return status != null && authErrorStatuses.includes(status)
}

// tenantSettings 専用: logout すべきエラーか（401/403 or 404 + code 917/2000）
// NOTE: TB-1882 の BE 修正後、/berths/tenant は PermissionException を 404 "917" に詰め替えるため
// 403 は到達しない。401/403 判定は他要因（認証切れ・インフラ層）への防御として残している。
export const isTenantSettingsLogoutError = (err: unknown): boolean =>
  isAuthError(err) ||
  (getHttpStatus(err) === HTTP_STATUS.NOT_FOUND &&
    tenantSettingsLogoutErrorCodes.includes(getFirstApiErrorCode(err) ?? ''))
